<template>
  <div class="tournament-presentation">
    <b-loading :is-full-page="true" v-model="isLoading"></b-loading>

    <div
      v-if="!isLoading && (!tournament || !currentRound)"
      class="error-message"
    >
      <div class="error-content">
        <h2>Unable to load tournament data</h2>
        <p>Please check if the tournament exists and try again.</p>
      </div>
    </div>

    <div
      v-else-if="!isLoading && tournament && currentRound"
      class="presentation-container"
    >
      <div class="presentation-header">
        <div class="tournament-round-title">
          {{ tournament.name || "Tournament" }} - Round
          {{ currentRound.roundNumber }}
        </div>
      </div>

      <div class="presentation-main">
        <!-- no paring -->
        <div
          v-if="pairedGames.length === 0"
          class="subtitle is-4 has-text-danger"
        >
          No pairing
        </div>

        <!-- has parings -->
        <div
          v-else
          class="games-container"
          :class="{ 'single-column': shouldUseOneColumn }"
        >
          <div
            v-for="(columnGames, columnIndex) in gameColumns"
            :key="columnIndex"
            class="games-column"
            :style="{
              gridTemplateRows: `repeat(${maxGamesPerColumn}, 1fr)`,
            }"
          >
            <div
              v-for="(game, index) in columnGames"
              :key="game.gameId"
              class="game-row"
              :class="{
                even: index % 2 === 0,
                odd: index % 2 === 1,
                'left-column': columnIndex === 0,
                'right-column': columnIndex > 0,
              }"
            >
              <span class="board-number">{{ game.board }}</span>
              <span class="white-player">
                <span class="player-name">{{
                  getPlayerDisplayName(game.whiteUsername)
                }}</span>
                <span class="player-score"
                  >({{ getPlayerScore(game.whiteUsername) }})</span
                >
              </span>

              <span class="separator">
                <span v-if="getGameResult(game)" class="has-text-primary">{{
                  getGameResult(game)
                }}</span>
                <span v-else style="line-height: 4vh">
                  <span style="font-size: 4vh">♙</span>⚔️<span
                    style="font-size: 4vh"
                    >♟</span
                  >
                </span>
              </span>

              <span class="black-player">
                <span class="player-name">{{
                  getPlayerDisplayName(game.blackUsername)
                }}</span>
                <span v-if="game.blackUsername" class="player-score"
                  >({{ getPlayerScore(game.blackUsername) }})</span
                >
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { firestore } from "@/services/firebase";
import { mapGetters } from "vuex";
import {
  TOURNAMENT_FIDE_GAME_RESULT,
  TOURNAMENT_GAME_RESULT,
} from "vue-chess-shared/constants";

export default {
  name: "TournamentRoundPresentation",

  data() {
    return {
      isLoading: true,
      tournament: null,
      players: [],
      rounds: [],
      games: [],
    };
  },

  computed: {
    ...mapGetters({
      classTournamentState: "getClassTournamentState",
    }),

    currentRound() {
      if (!this.rounds || !this.classTournamentState.selectedRoundNumber)
        return null;
      return this.rounds.find(
        (r) => r.roundNumber === this.classTournamentState.selectedRoundNumber
      );
    },

    pairedGames() {
      if (!this.games || !this.classTournamentState.selectedRoundNumber)
        return [];

      return this.games
        .filter(
          (game) =>
            game.roundNumber ===
              this.classTournamentState.selectedRoundNumber &&
            game.whiteUsername
        )
        .sort((a, b) => a.board - b.board);
    },

    maxGamesPerColumn() {
      return this.shouldUseOneColumn
        ? Math.max(10, this.pairedGames.length)
        : Math.max(10, Math.ceil(this.pairedGames.length / 2));
    },

    shouldUseOneColumn() {
      return this.pairedGames.length <= 12;
    },

    gameColumns() {
      if (this.shouldUseOneColumn) {
        // Single column for 13 or fewer games
        return [this.pairedGames];
      } else {
        // Two columns for more than 13 games
        const columns = [];
        const gamesPerColumn = Math.ceil(this.pairedGames.length / 2);
        for (let i = 0; i < this.pairedGames.length; i += gamesPerColumn) {
          columns.push(this.pairedGames.slice(i, i + gamesPerColumn));
        }
        return columns;
      }
    },
  },

  methods: {
    findPlayer(username) {
      return this.players.find((p) => p.username === username);
    },

    getPlayerDisplayName(username) {
      if (!username) return "";

      const player = this.findPlayer(username);
      if (player && player.user) {
        return (
          player.user.first_name + " " + player.user.last_name ||
          player.user.username ||
          username
        );
      }
      return username;
    },

    getPlayerScore(username) {
      if (!username || !this.currentRound || !this.currentRound.standings) {
        return 0;
      }

      const standing = this.currentRound.standings.find(
        (s) => s.player.id === username || s.player.username === username
      );

      return standing ? standing.matchPoints || 0 : 0;
    },

    getGameResult(game) {
      // Check if game has results
      const hasResults =
        game.whiteResult !== undefined &&
        game.blackResult !== undefined &&
        game.whiteResult !== TOURNAMENT_GAME_RESULT.UNFINISHED &&
        game.blackResult !== TOURNAMENT_GAME_RESULT.UNFINISHED;

      if (hasResults) {
        // Show numeric results instead of pawn icons
        const whiteScore = this.getScoreFromResult(game.whiteResult);
        const blackScore = this.getScoreFromResult(game.blackResult);
        if (whiteScore === "0" || whiteScore === "1") {
          return `${whiteScore}-${blackScore}`;
        }
        return `${whiteScore}`;
      }
      return "";
    },

    getScoreFromResult(result) {
      return TOURNAMENT_FIDE_GAME_RESULT[result] || "";
    },
  },

  async created() {
    try {
      const tournamentId = this.$route.params.id;

      if (!tournamentId) {
        console.error("No tournament ID provided");
        this.isLoading = false;
        return;
      }

      const tournamentRef = firestore
        .collection("classTournaments")
        .doc(tournamentId);

      const roundsRef = tournamentRef.collection("rounds");
      const gamesRef = tournamentRef.collection("games");
      const playersRef = tournamentRef.collection("players");

      // Use VueFire bindings for real-time updates
      await this.$bind("tournament", tournamentRef);
      await this.$bind("players", playersRef);
      await this.$bind("rounds", roundsRef);
      await this.$bind("games", gamesRef);

      this.isLoading = false;
    } catch (error) {
      console.error("Error loading tournament data:", error);
      this.isLoading = false;
    }
  },
};
</script>

<style scoped>
.tournament-presentation {
  color: #2c3e50;
  z-index: 32;
  box-sizing: border-box;
  overflow: hidden;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  display: flex;
  flex-direction: column;
  margin: 0 !important;
  padding: 0 !important;
}

.presentation-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.presentation-header {
  text-align: center;
  flex-shrink: 0;
  padding: 0.5em;
}

.tournament-round-title {
  font-size: 3.5vh;
  font-weight: bold;
}

.presentation-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  padding: 0 2vh 2vh 2vh;
  min-height: 0;
  height: 100%;
}

.games-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  flex: 1;
  overflow: hidden;
  min-height: 0;
  height: 100%;
  width: 100%;
}

.games-container.single-column {
  grid-template-columns: 1fr;
}

.games-column {
  display: grid;
  gap: 0;
  height: 100%;
}

.single-column .games-column {
  width: 100%;
}

.game-row {
  display: grid;
  grid-template-columns: auto 1fr auto 1fr;
  gap: 0.5vw;
  align-items: center;
  padding: 0.25em;
  font-size: 2.6vh;
  font-weight: 500;
  border: 1px solid #dee2e6;
  flex: 1;
}

/* Left column styling */
.game-row.left-column.even {
  background: #e3f2fd;
}

.game-row.left-column.odd {
  background: #bbdefb;
}

/* Right column styling */
.game-row.right-column.even {
  background: #bbdefb;
}

.game-row.right-column.odd {
  background: #e3f2fd;
}

.board-number {
  text-align: right;
  font-weight: bold;
  color: #1565c0;
  width: 1.5em;
}

.white-player {
  text-align: right;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-width: 0;
}

.player-name {
  font-weight: 600;
  color: #2c3e50;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex-shrink: 1;
}

.player-score {
  font-size: 2vh;
  font-weight: 300;
  color: #2c3e50;
}

.separator {
  font-size: 3vh;
  font-family: monospace;
  width: 5.5vw;
  color: #6c757d;
  text-align: center;
  font-weight: bold;
}

.black-player {
  text-align: left;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  min-width: 0;
}

.error-message {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2vh;
}

.error-content {
  text-align: center;
}

.error-content h2 {
  font-size: 4vh;
  margin-bottom: 2vh;
  color: #e74c3c;
}

.error-content p {
  font-size: 2.5vh;
  color: #7f8c8d;
}
</style>

<style>
/* Hide navbar and other elements when presentation is active */
.tournament-presentation {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  z-index: 99999 !important;
}
</style>
